import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clinicFaqs } from '@/mocks/corporateClinicData';

function splitParagraphs(value: string): string[] {
  return value.split(/\n{2,}/).map((part) => part.trim()).filter((part) => part.length > 0);
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: clinicFaqs.map((_faq, idx) => ({
      '@type': 'Question',
      name: t(`corporateClinic.faq.${idx}.q`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: String(t(`corporateClinic.faq.${idx}.a`)).replace(/\n+/g, ' '),
      },
    })),
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <div className="max-w-[800px] mx-auto px-8">
        <div className="text-center mb-14">
          {splitParagraphs(String(t('corporateClinic.faq.label'))).map((para, i) => (
            <p key={i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
              {para}
            </p>
          ))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporateClinic.faq.title')}
          </h2>
          {splitParagraphs(String(t('corporateClinic.faq.titleEn'))).map((para, i) => (
            <p key={i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">
              {para}
            </p>
          ))}
          <div className="gold-line mx-auto"></div>
        </div>

        <div className="space-y-3">
          {clinicFaqs.map((_faq, idx) => (
            <div
              key={idx}
              className="border border-background-200/60 bg-background-100/60"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-[16px] text-foreground-800 tracking-[0.04em] font-medium pr-4">
                  {t(`corporateClinic.faq.${idx}.q`)}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-accent-500 text-sm transition-transform duration-300 ${
                      openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? 'max-h-[300px]' : 'max-h-0'
                }`}
              >
                {splitParagraphs(String(t(`corporateClinic.faq.${idx}.a`))).map((para, paraIdx) => (
                  <p key={paraIdx} className="px-5 pb-5 text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {splitParagraphs(String(t('corporateClinic.faq.note'))).map((para, i) => (
          <p key={i} className="text-center text-[14px] text-foreground-400 tracking-[0.04em] mt-8">
            {para}
          </p>
        ))}
      </div>
    </section>
  );
}
